import { useQuery } from '@tanstack/react-query'
import { Flex, Space, Tabs, Tag } from 'antd'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { organizationsOrganizationsCustomAuthProvidersListOptions } from 'src/api/main'
import { useFeatureAuthPolicy } from 'src/flags'

import { CustomProvidersTab } from './CustomProvidersTab'
import { PolicyTab } from './PolicyTab'
import { SignInMethodsTab } from './SignInMethodsTab'
import { useStyles } from './styles'
import { selectCustomProviders } from './utils'

const TAB_KEYS = ['signIn', 'customProviders', 'policy'] as const
type TabKey = (typeof TAB_KEYS)[number]

const isTabKey = (v: string | null): v is TabKey =>
  v != null && (TAB_KEYS as readonly string[]).includes(v)

const LEGACY_TAB_KEYS: Record<string, TabKey> = {
  ssoScim: 'customProviders'
}

type Props = {
  workspaceId: number
  idValid: boolean
}

export const WorkspaceAuthenticationPanel = ({
  workspaceId,
  idValid
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const [searchParams, setSearchParams] = useSearchParams()

  // P1 scope gating: the Policy tab (policy enforcement / risk-based) is static
  // only — hidden until a real policy backend lands. Custom providers are NOT
  // gated (live API).
  const policyEnabled = useFeatureAuthPolicy()

  const rawTab = searchParams.get('tab')
  const mappedTab = rawTab ? LEGACY_TAB_KEYS[rawTab] : undefined
  const requestedTab: TabKey =
    mappedTab ?? (isTabKey(rawTab) ? rawTab : 'signIn')
  const activeTab: TabKey =
    requestedTab === 'policy' && !policyEnabled ? 'signIn' : requestedTab

  const setTab = useCallback(
    (key: TabKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (key === 'signIn') {
            next.delete('tab')
          } else {
            next.set('tab', key)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { data: customProviders = [] } = useQuery({
    ...organizationsOrganizationsCustomAuthProvidersListOptions({
      path: { id: workspaceId }
    }),
    enabled: idValid,
    select: selectCustomProviders
  })

  return (
    <Flex vertical gap={14} className={styles.panelRoot}>
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setTab(k as TabKey)}
        tabBarStyle={{ marginBottom: 0 }}
        items={[
          {
            key: 'signIn',
            label: t('workspace.authentication.tabs.signIn')
          },
          {
            key: 'customProviders',
            label: (
              <Space size={4}>
                {t('workspace.authentication.tabs.customProviders')}
                <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
                  {customProviders.length}
                </Tag>
              </Space>
            )
          },
          ...(policyEnabled
            ? [
                {
                  key: 'policy',
                  label: t('workspace.authentication.tabs.policy')
                }
              ]
            : [])
        ]}
      />

      {activeTab === 'signIn' && (
        <SignInMethodsTab workspaceId={workspaceId} idValid={idValid} />
      )}
      {activeTab === 'customProviders' && (
        <CustomProvidersTab workspaceId={workspaceId} idValid={idValid} />
      )}
      {activeTab === 'policy' && policyEnabled && (
        <PolicyTab workspaceId={workspaceId} idValid={idValid} />
      )}
    </Flex>
  )
}
